import { Trans, t } from "@lingui/macro";

import { BESC } from "config/chains";
import { USD_DECIMALS } from "config/factors";

import { getIsSyntheticsSupported } from "config/features";
import { getWhitelistedV1Tokens } from "config/tokens";
import { SyntheticsStateContextProvider } from "context/SyntheticsStateContext/SyntheticsStateContextProvider";
import { useMoneyPrice, useTotalMoneyInLiquidity, useTotalMoneySupply } from "domain/legacy";
import { useInfoTokens } from "domain/tokens";
import { bigMath } from "lib/bigmath";
import { useChainId } from "lib/chains";
import { MLP_DECIMALS, MONEY_DECIMALS, getPageTitle } from "lib/legacy";
import { expandDecimals } from "lib/numbers";
import { useTradePageVersion } from "lib/useTradePageVersion";
import useWallet from "lib/wallets/useWallet";
import { useDashboardChainStatsMulticall } from "./useDashboardChainStatsMulticall";

import SEO from "components/Common/SEO";
import ExternalLink from "components/ExternalLink/ExternalLink";
import Footer from "components/Footer/Footer";
import PageTitle from "components/PageTitle/PageTitle";
import { MarketsList } from "components/Synthetics/MarketsList/MarketsList";
import { DashboardPageTitle } from "./DashboardPageTitle";
import { MlpCard } from "./MlpCard";
import { GmCard } from "./GmCard";
import { MoneyCard } from "./MoneyCard";
import { MarketsListV1 } from "./MarketsListV1";
import { OverviewCard } from "./OverviewCard";
import { StatsCard } from "./StatsCard";

import "./DashboardV2.css";

export const ACTIVE_CHAIN_IDS = [BESC];

export default function DashboardV2() {
  const { active, signer } = useWallet();
  const { chainId } = useChainId();

  const [tradePageVersion] = useTradePageVersion();

  const isV1 = tradePageVersion === 1;
  const isV2 = tradePageVersion === 2;

  let { total: totalMoneySupply } = useTotalMoneySupply();

  const statsBesc = useDashboardChainStatsMulticall(BESC);

  const totalTokenWeights = statsBesc?.vault.totalTokenWeights;

  const mlpTvl = statsBesc?.mlp.aum;
  const mlpSupply = statsBesc?.reader.tokenBalancesWithSupplies?.mlpSupply;

  const { infoTokens } = useInfoTokens(signer, BESC, active, undefined, undefined);

  const { moneyPrice, moneyPriceFromBsc } = useMoneyPrice(chainId);

  let moneyMarketCap =
    moneyPrice !== undefined && totalMoneySupply !== undefined
      ? bigMath.mulDiv(moneyPrice, totalMoneySupply, expandDecimals(1, MONEY_DECIMALS))
      : undefined;

  let { total: totalMoneyInLiquidity } = useTotalMoneyInLiquidity();

  const mlpPrice =
    mlpTvl !== undefined && mlpTvl > 0n && mlpSupply !== undefined
      ? bigMath.mulDiv(mlpTvl, expandDecimals(1, MLP_DECIMALS), mlpSupply)
      : expandDecimals(1, USD_DECIMALS);

  const mlpMarketCap =
    mlpSupply !== undefined
      ? bigMath.mulDiv(mlpPrice, mlpSupply, expandDecimals(1, MLP_DECIMALS))
      : undefined;

  const whitelistedTokens = getWhitelistedV1Tokens(BESC);
  const tokenList = whitelistedTokens.filter((t) => !t.isWrapped);

  let adjustedUsdgSupply = 0n;
  for (let i = 0; i < tokenList.length; i++) {
    const token = tokenList[i];
    const tokenInfo = infoTokens[token.address.toLowerCase()];
    if (tokenInfo && tokenInfo.usdgAmount !== undefined) {
      adjustedUsdgSupply = adjustedUsdgSupply + tokenInfo.usdgAmount;
    }
  }

  return (
    <SEO title={getPageTitle(t`Dashboard`)}>
      <div className="default-container DashboardV2 page-layout">
        <PageTitle
          title={t`Total Stats`}
          showNetworkIcon={false}
          isTop
          qa="dashboard-page"
          subtitle={
            <div>
              <Trans>For detailed stats:</Trans>{" "}
              {/* {chainId === ARBITRUM && <ExternalLink href="https://stats.money.io">V1 Analytics</ExternalLink>}
              {chainId === BSC && <ExternalLink href="https://stats.money.io/bsc">V1 Analytics</ExternalLink>}
              {(chainId === ARBITRUM || chainId === BSC) && " | "}
              <ExternalLink href="https://dune.com/money-io/money-analytics">V2 Analytics</ExternalLink>
              {" | "}
              <ExternalLink href="https://moneyxpro.com/#/stats">V2 Pools Stats</ExternalLink>. */}
              <a href={import.meta.env.VITE_STATS_URL || "https://stats.moneyxpro.com"} target="_blank" rel="noopener noreferrer" style={{ color: "var(--gold)", textDecoration: "underline" }}>Analytics</a>
            </div>
          }
        />
        <div className="DashboardV2-content">
          <div className="DashboardV2-cards">
            <OverviewCard statsBesc={statsBesc} />
            <StatsCard statsBesc={statsBesc} />
          </div>
          <DashboardPageTitle tradePageVersion={tradePageVersion} />
          <div className="DashboardV2-token-cards">
            <div className="stats-wrapper stats-wrapper--money">
              <MoneyCard
                chainId={BESC}
                moneyPrice={moneyPrice}
                moneyPriceFromArbitrum={undefined}
                moneyPriceFromBsc={moneyPriceFromBsc}
                totalMoneySupply={totalMoneySupply}
                moneyMarketCap={moneyMarketCap}
                totalMoneyInLiquidity={totalMoneyInLiquidity}
              />
              {isV1 && (
                <MlpCard
                  chainId={BESC}
                  mlpPrice={mlpPrice}
                  mlpSupply={mlpSupply}
                  mlpMarketCap={mlpMarketCap}
                  adjustedUsdgSupply={adjustedUsdgSupply}
                />
              )}
              {isV2 && <GmCard />}
            </div>
            {isV1 && (
              <MarketsListV1
                chainId={BESC}
                infoTokens={infoTokens}
                totalTokenWeights={totalTokenWeights}
                adjustedUsdgSupply={adjustedUsdgSupply}
              />
            )}
            {isV2 && getIsSyntheticsSupported(chainId) && (
              <SyntheticsStateContextProvider skipLocalReferralCode={false} pageType="pools">
                <MarketsList />
              </SyntheticsStateContextProvider>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </SEO>
  );
}
